import PropTypes from 'prop-types';
import React from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import { Arrow, Bar, mix, Pill, positioned, Tile } from './illustration_parts';

export const ILLUSTRATION_WIDTH = 552;

export default function AgenticCommerceUpsellIllustration({ scale }) {
  const { t } = useTranslation();

  return (
    <Canvas $scale={scale}>
      <div aria-hidden="true">
        <EngagementTile />
        <ComparePeriodsTile />
        <CustomersOrderedTile />
        <Fade $direction="down" $height={214} $left={0} $top={356} $width={528} />
        <OrdersTableTile />
        <Fade $direction="up" $height={211} $left={2} $top={0} $width={526} />
      </div>
      <StatCard $gradients={CARD_GRADIENTS.engagement} $left={0}>
        <StatValue>{t('3.1×')}</StatValue>
        <StatLabel>{t('Higher customer engagement')}</StatLabel>
      </StatCard>
      <StatCard $gradients={CARD_GRADIENTS.cartAdds} $left={182}>
        <StatValue>{t('8.8×')}</StatValue>
        <StatLabel>{t('More cart adds per 100,000 visitors')}</StatLabel>
      </StatCard>
      <StatCard $gradients={CARD_GRADIENTS.conversion} $left={364}>
        <StatValue $isCompact>{t('10–13%')}</StatValue>
        <StatLabel>{t('Conversion rate')}</StatLabel>
      </StatCard>
      <Caption>{t('Average results for brands using Gladly’s Agentic Commerce')}</Caption>
    </Canvas>
  );
}

AgenticCommerceUpsellIllustration.propTypes = {
  scale: PropTypes.number.isRequired,
};

function OrdersTableTile() {
  return (
    <Tile $height={166} $left={71} $top={0} $width={390}>
      <Bar $color="green100" $height={22} $left={-1} $top={35} $width={390} />
      {[13, 108, 186, 303].map((left, i) => (
        <Pill $color="green200" $left={left} $top={43} $width={[50, 40, 70, 40][i]} key={left} />
      ))}
      <Bar $color="gray100" $height={22} $left={-1} $top={83} $width={390} />
      <Bar $color="gray100" $height={22} $left={-1} $top={131} $width={390} />
      {ORDER_ROWS.map(({ barWidth, top }) => (
        <React.Fragment key={top}>
          <Pill $color="gray400" $left={13} $top={top} $width={56} />
          <Pill $color="gray400" $left={108} $top={top} $width={34} />
          <Bar $color="green200" $height={12} $left={186} $radius={1} $top={top - 3} $width={barWidth} />
          <Pill $color="gray400" $left={303} $top={top} $width={34} />
        </React.Fragment>
      ))}
    </Tile>
  );
}

function EngagementTile() {
  return (
    <Tile $height={166} $left={41} $top={394} $width={223}>
      <Bar $color="gray400" $height={16} $left={15} $top={14} $width={151} />
      {FUNNEL_ROWS.map(({ color, top, width }) => (
        <React.Fragment key={top}>
          <Pill $color="gray200" $height={5} $left={15} $top={top} $width={191} />
          <Pill $color={color} $height={5} $left={15} $top={top} $width={width} />
        </React.Fragment>
      ))}
    </Tile>
  );
}

function ComparePeriodsTile() {
  return (
    <Tile $height={107} $left={277} $top={393} $width={210}>
      <Bar $color="gray400" $height={16} $left={15} $top={17} $width={119} />
      <Bar $color="gray300" $height={1} $left={15} $top={44} $width={181} />
      {[57, 74, 91].map(top => (
        <React.Fragment key={top}>
          <Pill $color="gray400" $left={16} $top={top} $width={40} />
          <Arrow $left={63} $top={top - 4}>
            ↑
          </Arrow>
          <Pill $color="green200" $left={72} $top={top} $width={26} />
          <Pill $color="gray400" $left={115} $top={top + 1} $width={40} />
          <Arrow $left={162} $top={top - 3}>
            ↑
          </Arrow>
          <Pill $color="green200" $left={171} $top={top + 1} $width={26} />
        </React.Fragment>
      ))}
    </Tile>
  );
}

function CustomersOrderedTile() {
  return (
    <Tile $height={52} $left={277} $top={508} $width={210}>
      <Bar $color="green200" $height={16} $left={14} $top={21} $width={100} />
      <Bar $color="gray500" $height={16} $left={94} $top={21} $width={49} />
      <Bar $color="gray400" $height={16} $left={143} $top={21} $width={30} />
      <Bar $color="gray200" $height={16} $left={173} $top={21} $width={21} />
    </Tile>
  );
}

const ORDER_ROWS = [
  { barWidth: 12, top: 65 },
  { barWidth: 76, top: 89 },
  { barWidth: 60, top: 113 },
  { barWidth: 40, top: 137 },
];

const FUNNEL_ROWS = [
  { color: 'green100', top: 49, width: 151 },
  { color: 'green200', top: 67, width: 133 },
  { color: 'green300', top: 85, width: 117 },
  { color: 'green400', top: 103, width: 59 },
  { color: 'green500', top: 121, width: 40 },
  { color: 'green600', top: 139, width: 26 },
];

// Each card's radial highlights, top layer first: [color token, opacity, center x, center y, radius x, radius y, solid stop %]
const CARD_GRADIENTS = {
  engagement: [
    ['white', 60, 164, 95.2, 114.7, 118.9, 4.9],
    ['white', 85, 41, 37.4, 106.5, 110.4, 15],
    ['green300', 22, 157.4, 30.6, 124.2, 128.8, 6.7],
    ['green200', 55, 42.6, 151.3, 113.9, 118.1, 3.3],
  ],
  cartAdds: [
    ['white', 60, 16.4, 51, 108.5, 112.4, 5.2],
    ['white', 85, 134.5, 132.6, 122.5, 127, 16.7],
    ['green300', 22, 9.8, 163.2, 127.8, 132.5, 6.9],
    ['green200', 55, 141, 17, 126.6, 131.2, 6.5],
  ],
  conversion: [
    ['white', 60, 32.8, 142.8, 114.1, 118.3, 6.7],
    ['white', 85, 144.3, 44.2, 116.9, 121.2, 14.5],
    ['green300', 24, 155.8, 159.8, 131.5, 136.3, 6.7],
    ['green200', 50, 13.1, 10.2, 125.1, 129.7, 5.2],
  ],
};

// Figma's monotone noise effect (size 1.5, density 0.4) in green600 at 5%.
const NOISE =
  "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='120' height='120'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.67' numOctaves='3' stitchTiles='stitch'/><feColorMatrix values='0 0 0 0 0.07 0 0 0 0 0.34 0 0 0 0 0.08 0 0 0 1.3 -0.6'/></filter><rect width='100%' height='100%' filter='url(%23n)' opacity='0.05'/></svg>\")";

function cardBackground(p) {
  const highlights = p.$gradients.map(
    ([token, opacity, x, y, rx, ry, stop]) =>
      `radial-gradient(${rx}px ${ry}px at ${x}px ${y}px, ${mix(p, token, opacity)} ${stop}%, transparent 100%)`
  );
  return [NOISE, ...highlights, `linear-gradient(${mix(p, 'green100', 20)}, ${mix(p, 'green100', 20)})`].join(', ');
}

const Canvas = styled.div`
  height: 570px;
  margin: 12px;
  position: relative;
  width: 528px;
  zoom: ${p => p.$scale};
`;

// Figma's Glass effect (radius 5) on a fade from 40% to 20% of the page background.
const Fade = styled.div`
  ${positioned};
  backdrop-filter: blur(5px);
  background: linear-gradient(
    ${p => (p.$direction === 'down' ? 'to bottom' : 'to top')},
    color-mix(in srgb, var(--color-default-background-color) 40%, transparent),
    color-mix(in srgb, var(--color-default-background-color) 20%, transparent)
  );
`;

const StatCard = styled.div`
  background-image: ${cardBackground};
  background-origin: border-box;
  border: 1.5px solid ${p => p.theme.colors.green200};
  border-radius: 12px;
  box-shadow: ${p => p.theme.boxShadow.medium};
  box-sizing: border-box;
  height: 170px;
  left: ${p => p.$left}px;
  position: absolute;
  top: 182px;
  width: 164px;
`;

// Text positions and line heights are the design's text boxes, measured from the card's outer edge, so the
// card's 1.5px border is subtracted. The big numbers use a 42px line height; "10–13%" and the labels use normal.
const STAT_TEXT_LEFT = 16 - 1.5;

const StatValue = styled.div`
  color: ${p => p.theme.colors.green500};
  font-size: ${p => (p.$isCompact ? 36 : 42)}px;
  font-weight: ${p => p.theme.fontWeight.heavy};
  left: ${STAT_TEXT_LEFT}px;
  line-height: ${p => (p.$isCompact ? 'normal' : '42px')};
  position: absolute;
  top: ${p => (p.$isCompact ? 47 : 46) - 1.5}px;
  white-space: nowrap;
`;

const StatLabel = styled.div`
  color: ${p => p.theme.colors.black};
  font-size: 14px;
  left: ${STAT_TEXT_LEFT}px;
  line-height: normal;
  position: absolute;
  top: ${97 - 1.5}px;
  width: 133px;
`;

const Caption = styled.div`
  color: ${p => p.theme.colors.gray800};
  font-size: 12px;
  left: 0;
  line-height: 18px;
  position: absolute;
  right: 0;
  text-align: center;
  top: 365px;
`;
